import { React } from 'Imports';
import { FormControl, Select } from 'MaterialUIComponents';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { Box, Button, MenuItem, MenuProps } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import DropdownSearchField from '$Components/FilterComponents/Filters/DropdownSearchField';
import DropdownMenuItemCheckbox from '$Components/FilterComponents/Filters/DropdownMenuItemCheckbox';
import * as scssStyles from '$CSS/settings.scss';

const baseStyles = require('$Components/FilterComponents/Filters/SelectBase.scss') as {
    selectIcon: string;
    noResultsMessage: string;
    scrollAreaEncompass: string;
    actionButtonsContainer: string;
    closeButtonEncompass: string;
    applyButtonEncompass: string;
};

const styles = require('$Components/FilterComponents/Filters/CompanyLevelFilter.scss') as {
    viewLabel: string;
    viewLabelBold: string;
    body: string;
    section: string;
    eyebrow: string;
    row: string;
    rowNoBottomPadding: string;
    rowSeparator: string;
    rowLabel: string;
    rowLabelSelected: string;
    rightHint: string;
    checkboxLabel: string;
    actionButtonsBottomPadding: string;
};

interface ICompanyLevelFilterProps {
    fleetName: string;
    items: IDropdownItem[];
    selectedValues: string[];
    applyFilter: (selectedValues: string[]) => void;
    disabled?: boolean;
    isMobileView: boolean;
}

const buildSummary = (fleetName: string, selectedItems: IDropdownItem[], totalCount: number): string => {
    const allSelected = totalCount > 0 && selectedItems.length === totalCount;
    if (selectedItems.length === 0 || allSelected) {
        return `${fleetName} (All)`;
    }
    const labels = selectedItems.map((i) => i.label);
    if (labels.length === 1) {
        return labels[0];
    }
    if (labels.length === 2) {
        return `${labels[0]} and ${labels[1]}`;
    }
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

const CompanyLevelFilter = (props: ICompanyLevelFilterProps) => {
    const initialSelectedValuesRef = useRef<string[]>([]);

    const valuesToItems = (values: string[]): IDropdownItem[] =>
        (values || []).map((value) => props.items.find((i) => i.value === value)).filter((i): i is IDropdownItem => Boolean(i));

    const [selectedItems, setSelectedItems] = useState<IDropdownItem[]>(valuesToItems(props.selectedValues));
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownHasFocus, setDropdownHasFocus] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [selectionChanged, setSelectionChanged] = useState(false);
    const [wiggle, setWiggle] = useState(false);

    const isFiltering = searchFilter.length > 0;

    const filteredItems = isFiltering
        ? props.items.filter((item) => item.label.toLowerCase().includes(searchFilter.toLowerCase()))
        : props.items;

    const allSelected = props.items.length > 0 && selectedItems.length === props.items.length;
    const summary = buildSummary(props.fleetName, selectedItems, props.items.length);

    const dropdownMenuProps: Partial<MenuProps> = {
        transformOrigin: {
            horizontal: 'right',
            vertical: 'top',
        },
        anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom',
        },
        disablePortal: true,
        MenuListProps: {
            sx: { paddingTop: 0 },
        },
        slotProps: {
            root: { sx: { width: 'auto' } },
            paper: {
                sx: {
                    overflowY: 'hidden !important',
                    maxHeight: 420,
                    zIndex: 1300,
                    width: 'max-content',
                    minWidth: 357,
                    maxWidth: 'min(90vw, 560px)',
                },
                className: wiggle ? 'vp-wiggle' : '',
            },
        },
    };

    useEffect(() => {
        setSelectedItems(valuesToItems(props.selectedValues));
    }, [props.items, props.selectedValues]);

    useEffect(() => {
        const initialValues = initialSelectedValuesRef.current.slice().sort();
        const currentValues = selectedItems
            .map((i) => i.value as string)
            .slice()
            .sort();
        const valuesChanged =
            initialValues.length !== currentValues.length || !initialValues.every((value, index) => value === currentValues[index]);
        setSelectionChanged(valuesChanged);
    }, [selectedItems]);

    const handleOpen = () => {
        setIsOpen(true);
        setSearchFilter('');
        setDropdownHasFocus(true);
        initialSelectedValuesRef.current = selectedItems.map((i) => i.value as string);
    };

    const clearStates = () => {
        setIsOpen(false);
        setSearchFilter('');
        setDropdownHasFocus(false);
    };

    const handleClose = () => {
        clearStates();
        setSelectedItems(valuesToItems(initialSelectedValuesRef.current));
    };

    const handleApply = () => {
        clearStates();
        props.applyFilter(selectedItems.map((i) => i.value as string));
        initialSelectedValuesRef.current = selectedItems.map((i) => i.value as string);
        setSelectionChanged(false);
    };

    const handleItemClick = (item: IDropdownItem) => {
        if (selectedItems.some((i) => i.value === item.value)) {
            setSelectedItems(selectedItems.filter((i) => i.value !== item.value));
        } else {
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleSelectAll = () => {
        setSelectedItems(allSelected ? [] : props.items.slice());
    };

    const handleClickAway = () => {
        if (isOpen) {
            if (selectionChanged) {
                setWiggle(true);
                setTimeout(() => setWiggle(false), 300);
            } else {
                handleClose();
            }
        }
    };

    return (
        <FormControl sx={{ width: props.isMobileView ? '100%' : 'max-content' }} focused={dropdownHasFocus}>
            <Select
                labelId="company-level-select-label"
                id="company-level-select"
                multiple
                inputProps={{
                    'aria-label': 'Company Level',
                    sx: { padding: '0 20px 0 0 !important', display: 'flex', alignItems: 'center' },
                }}
                displayEmpty
                value={[]}
                disabled={props.disabled || false}
                MenuProps={dropdownMenuProps}
                renderValue={() => (
                    <div className={styles.viewLabel}>
                        <span className={styles.viewLabelBold}>View:</span>
                        {summary}
                    </div>
                )}
                IconComponent={() => (
                    <Box className={baseStyles.selectIcon} onClick={isOpen ? handleClose : handleOpen}>
                        <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} color={scssStyles.videoEventFilterSelectIconColor} />
                    </Box>
                )}
                open={isOpen}
                onClose={handleClickAway}
                onOpen={handleOpen}
                autoFocus={false}
                sx={{
                    cursor: 'pointer',
                    width: props.isMobileView ? '100%' : 200,
                    height: 32,
                    backgroundColor: '#fff',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                    },
                    '& .MuiSelect-select:focus': { backgroundColor: '#fff' },
                    fieldset: {
                        border: dropdownHasFocus ? '1px solid #0D7AC5 !important' : '1px solid #C4CBD4 !important',
                    },
                }}
            >
                <DropdownSearchField
                    onChange={setSearchFilter}
                    searchFilter={searchFilter}
                    onSearchFieldFocus={() => setDropdownHasFocus(false)}
                    onSearchFieldFocusLost={() => setDropdownHasFocus(true)}
                    menuItemSx={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}
                    expandedSearchBar
                    inputSx={{
                        height: '37px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        borderRadius: '3px',
                    }}
                />

                <Box className={baseStyles.scrollAreaEncompass}>
                    <div className={styles.body}>
                        {!isFiltering && (
                            <div className={styles.section}>
                                <span className={styles.eyebrow}>Company Levels</span>
                                <MenuItem
                                    value="company-levels-all"
                                    disableRipple
                                    sx={{ padding: 0, '&:hover': { backgroundColor: 'transparent' } }}
                                    onClick={handleSelectAll}
                                >
                                    <div className={`${styles.row} ${styles.rowNoBottomPadding}`}>
                                        <div className={styles.checkboxLabel}>
                                            <DropdownMenuItemCheckbox checked={allSelected} onChange={handleSelectAll} />
                                            <span className={`${styles.rowLabel} ${allSelected ? styles.rowLabelSelected : ''}`}>
                                                {`${props.fleetName} (All)`}
                                            </span>
                                        </div>
                                        <span className={styles.rightHint}>Includes all reporting levels</span>
                                    </div>
                                </MenuItem>
                            </div>
                        )}

                        <div className={styles.section}>
                            <span className={styles.eyebrow}>Reporting Levels</span>
                            {filteredItems.map((item, index) => {
                                const checked = selectedItems.some((i) => i.value === item.value);
                                return (
                                    <MenuItem
                                        key={String(item.value)}
                                        value={String(item.value)}
                                        disableRipple
                                        sx={{ padding: 0, '&:hover': { backgroundColor: 'transparent' } }}
                                        onClick={() => handleItemClick(item)}
                                    >
                                        <div className={`${styles.row} ${index < filteredItems.length - 1 ? styles.rowSeparator : ''}`}>
                                            <div className={styles.checkboxLabel}>
                                                <DropdownMenuItemCheckbox checked={checked} onChange={() => handleItemClick(item)} />
                                                <span className={`${styles.rowLabel} ${checked ? styles.rowLabelSelected : ''}`}>
                                                    {item.label}
                                                </span>
                                            </div>
                                            {item.secondaryLabel && <span className={styles.rightHint}>{item.secondaryLabel}</span>}
                                        </div>
                                    </MenuItem>
                                );
                            })}
                        </div>
                    </div>
                </Box>

                {isFiltering && filteredItems.length === 0 && <div className={baseStyles.noResultsMessage}>No results found</div>}

                <Box className={`${baseStyles.actionButtonsContainer} ${styles.actionButtonsBottomPadding}`}>
                    <Button
                        variant="outlined"
                        size="small"
                        disableRipple
                        className={baseStyles.closeButtonEncompass}
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disableRipple
                        size="small"
                        className={baseStyles.applyButtonEncompass}
                        onClick={handleApply}
                        disabled={!selectionChanged}
                    >
                        Apply
                    </Button>
                </Box>
            </Select>
        </FormControl>
    );
};

export default CompanyLevelFilter;
